import { useState } from "react";
import { api } from "../lib/api";
import { Sparkle, ArrowRight, CurrencyDollar, CheckCircle } from "@phosphor-icons/react";

const JOB_TYPES = [
  { key: "interior", label: "Interior" },
  { key: "exterior", label: "Exterior" },
  { key: "commercial", label: "Commercial" },
  { key: "full_house", label: "Full house" },
];
const CONDITIONS = [
  { key: "good", label: "Good — clean walls" },
  { key: "average", label: "Average — minor patching" },
  { key: "needs_prep", label: "Needs prep — repairs/sanding" },
];
const EXTRAS_OPTIONS = ["ceilings", "trim/skirting", "doors", "feature wall", "window frames"];

export default function QuoteEstimator() {
  const [jobType, setJobType] = useState("interior");
  const [scope, setScope] = useState("");
  const [condition, setCondition] = useState("good");
  const [extras, setExtras] = useState([]);
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const toggleExtra = (e) => setExtras((p) => (p.includes(e) ? p.filter((x) => x !== e) : [...p, e]));

  const submit = async (e) => {
    e.preventDefault();
    if (!scope.trim()) {
      setError("Tell us a bit about the scope first.");
      return;
    }
    setError("");
    setLoading(true);
    setResult(null);
    try {
      const r = await api.post("/quote/estimate", {
        job_type: jobType,
        rooms_or_size: scope,
        surface_condition: condition,
        extras,
        description,
      });
      setResult(r.data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong, please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="estimator"
      data-testid="estimator-section"
      className="relative py-20 md:py-32 px-5 md:px-10 lg:px-16 bg-[#1A1A1A] text-[#F2F2EF] grain"
    >
      <div className="grid grid-cols-12 gap-6 md:gap-10">
        <div className="col-span-12 lg:col-span-4">
          <p className="section-num mb-3" style={{ color: "#D85C3A" }}>04 — AI Estimator</p>
          <h2 className="font-display text-5xl md:text-6xl uppercase tracking-tighter leading-[0.92]">
            Get an instant<br />ballpark.<br />
            <span className="text-[#D85C3A]">Powered by AI.</span>
          </h2>
          <p className="mt-6 text-[#bbb] max-w-md">
            Describe your job in plain English. Our trained estimator returns a price range,
            line-item breakdown and assumptions in about 5 seconds. Final price always confirmed
            on-site.
          </p>
          <div className="mt-8 flex items-center gap-3 text-sm text-[#999]">
            <Sparkle size={20} weight="duotone" className="text-[#D85C3A]" />
            Built on Claude Sonnet 4.5 · No spam, no calls — instant only.
          </div>
        </div>

        <form
          onSubmit={submit}
          className="col-span-12 lg:col-span-8 border-2 border-[#F2F2EF] bg-[#222] p-6 md:p-10"
          data-testid="estimator-form"
        >
          <div>
            <label className="text-xs uppercase tracking-widest text-[#bbb]">Job type</label>
            <div className="flex flex-wrap gap-2 mt-2">
              {JOB_TYPES.map((j) => (
                <button
                  type="button"
                  key={j.key}
                  data-testid={`jobtype-${j.key}`}
                  onClick={() => setJobType(j.key)}
                  className={`chip ${jobType === j.key ? "active" : ""}`}
                  style={jobType === j.key ? { background: "#D85C3A", color: "#fff", borderColor: "#D85C3A" } : { background: "transparent", color: "#F2F2EF" }}
                >
                  {j.label}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-6">
            <label className="text-xs uppercase tracking-widest text-[#bbb]">Scope (rooms or sqm)</label>
            <input
              data-testid="estimator-scope"
              className="input-brut mt-2 text-[#F2F2EF]"
              style={{ borderBottomColor: "#F2F2EF", color: "#F2F2EF" }}
              placeholder="e.g. 3 bedrooms, living, kitchen"
              value={scope}
              onChange={(e) => setScope(e.target.value)}
            />
          </div>

          <div className="mt-6">
            <label className="text-xs uppercase tracking-widest text-[#bbb]">Surface condition</label>
            <div className="flex flex-wrap gap-2 mt-2">
              {CONDITIONS.map((c) => (
                <button
                  type="button"
                  key={c.key}
                  data-testid={`condition-${c.key}`}
                  onClick={() => setCondition(c.key)}
                  className="chip"
                  style={condition === c.key
                    ? { background: "#D85C3A", color: "#fff", borderColor: "#D85C3A" }
                    : { background: "transparent", color: "#F2F2EF" }}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-6">
            <label className="text-xs uppercase tracking-widest text-[#bbb]">Add extras</label>
            <div className="flex flex-wrap gap-2 mt-2">
              {EXTRAS_OPTIONS.map((e) => (
                <button
                  type="button"
                  key={e}
                  data-testid={`extra-${e}`}
                  onClick={() => toggleExtra(e)}
                  className="chip"
                  style={extras.includes(e)
                    ? { background: "#6A7B61", color: "#fff", borderColor: "#6A7B61" }
                    : { background: "transparent", color: "#F2F2EF" }}
                >
                  {e}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-6">
            <label className="text-xs uppercase tracking-widest text-[#bbb]">Anything else? (optional)</label>
            <textarea
              data-testid="estimator-notes"
              rows={2}
              className="input-brut mt-2"
              style={{ borderColor: "#F2F2EF", color: "#F2F2EF", background: "transparent" }}
              placeholder="e.g. high ceilings, heritage cornices, weekend job"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          {error && <div className="mt-4 text-sm text-[#FFB4A2]">{error}</div>}

          <button
            type="submit"
            disabled={loading}
            data-testid="estimator-submit"
            className="mt-8 btn-brut disabled:opacity-60"
          >
            {loading ? "Crunching..." : "Estimate my job"} <ArrowRight size={20} weight="bold" />
          </button>

          {result && (
            <div data-testid="estimator-result" className="mt-8 border-2 border-[#F2F2EF] p-6 md:p-8 bg-[#F2F2EF] text-[#111] slide-up">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#D85C3A]">
                <CurrencyDollar size={18} weight="duotone" /> Estimated range
              </div>
              <div className="font-display text-5xl md:text-7xl mt-2 tracking-tighter">
                ${result.low_aud?.toLocaleString()} – ${result.high_aud?.toLocaleString()} <span className="text-base align-middle text-[#666]">{result.currency}</span>
              </div>
              <p className="mt-3 text-base">{result.summary}</p>

              {result.breakdown?.length > 0 && (
                <div className="mt-6">
                  <div className="text-xs uppercase tracking-widest text-[#666] mb-2">Breakdown</div>
                  <ul className="divide-y divide-[#1112]">
                    {result.breakdown.map((b, i) => (
                      <li key={`${b.item}-${i}`} className="flex justify-between py-2 text-sm">
                        <span>{b.item}</span>
                        <span className="font-bold">${b.estimate_aud}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {result.assumptions?.length > 0 && (
                <div className="mt-5">
                  <div className="text-xs uppercase tracking-widest text-[#666] mb-2">Assumptions</div>
                  <ul className="text-sm space-y-1">
                    {result.assumptions.map((a, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <CheckCircle size={16} weight="duotone" className="text-[#6A7B61] mt-0.5 flex-shrink-0" />
                        <span>{a}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <a href="#book" className="btn-brut mt-6">Book free site visit <ArrowRight size={18} weight="bold" /></a>

              {result.next_steps && (
                <p className="mt-4 text-xs text-[#666] italic">
                  {Array.isArray(result.next_steps) ? result.next_steps.join(" ") : result.next_steps}
                </p>
              )}
            </div>
          )}
        </form>
      </div>
    </section>
  );
}
